import type {
  EGlobalSearchItemType,
  IGlobalSearchGroup,
  IGlobalSearchItem,
  IGlobalSearchStaticEntry,
  TGlobalSearchGateResolver,
  TGlobalSearchTranslate,
} from "./global-search";
import type { IGlobalSearchIntegrationProviderCandidate } from "./global-search-client";
import type { IFaqCategory } from "./help";
import type { IProjectList } from "./projects";

// NOTE: NSAI-6853 — the category tabs of the global search palette. Each tab
// narrows the same result set the "All" tab shows; none of them has a source
// of its own. Shared so web and mobile offer the same tabs in the same order.
export enum EGlobalSearchTab {
  ALL = "all",
  CHATS = "chats",
  PROJECTS = "projects",
  ASSISTANTS = "assistants",
  PROMPTS = "prompts",
  SKILLS = "skills",
  AGENTS = "agents",
  GENERATED_FILES = "generated_files",
  KNOWLEDGE = "knowledge",
  MEETINGS = "meetings",
  INTEGRATIONS = "integrations",
  MODELS = "models",
  PAGES = "pages",
  SETTINGS = "settings",
  ORGANIZATION_SETTINGS = "organization_settings",
  HELP = "help",
}

// NOTE: What a tab lists while the search box is empty.
export enum EGlobalSearchTabBrowse {
  // NOTE: The recent chats list, infinitely scrolled — the palette's original
  // empty state.
  CHATS = "chats",
  // NOTE: NSAI-6853 — the first items of each kind the tab covers: the static
  // index and the lists the device already holds (projects, FAQ, connector
  // catalog), plus every source's default listing from GET /search/global
  // with `browse`.
  ITEMS = "items",
}

export interface IGlobalSearchTabDefinition {
  tab: EGlobalSearchTab;
  // NOTE: Key in the globalSearch namespace.
  labelKey: string;
  // NOTE: Empty for ALL, which keeps every type (unknown ones included).
  types: readonly EGlobalSearchItemType[];
  browse: EGlobalSearchTabBrowse;
  // NOTE: Some of the tab's results come from GET /search/global, so its
  // loading / error line applies to this tab.
  hasServerResults: boolean;
  // NOTE: EGlobalSearchGate values; the tab is offered only when all are open.
  gates: readonly string[];
}

export interface IIsGlobalSearchItemInTabProps {
  tab: EGlobalSearchTab;
  item: IGlobalSearchItem;
}

export interface IFilterGlobalSearchGroupsByTabProps {
  tab: EGlobalSearchTab;
  groups: readonly IGlobalSearchGroup[];
}

export interface IGetGlobalSearchTabsProps {
  isGateOpen: TGlobalSearchGateResolver;
}

export interface IIsGlobalSearchTabShowingTypeProps {
  tab: EGlobalSearchTab;
  type: EGlobalSearchItemType;
}

export interface IListGlobalSearchStaticEntriesProps {
  entries: readonly IGlobalSearchStaticEntry[];
  translate: TGlobalSearchTranslate;
  isGateOpen: TGlobalSearchGateResolver;
}

export interface IListGlobalSearchProjectItemsProps {
  projects: IProjectList | null;
  currentProjectUuid: string | null;
  personalLabel: string;
}

export interface IListGlobalSearchFaqItemsProps {
  categories: readonly IFaqCategory[];
  limit: number;
}

export interface IListGlobalSearchIntegrationProviderItemsProps {
  providers: readonly IGlobalSearchIntegrationProviderCandidate[];
  limit: number;
}

export interface IGetAdjacentGlobalSearchTabProps {
  tabs: readonly IGlobalSearchTabDefinition[];
  tab: EGlobalSearchTab;
  // NOTE: 1 for the next tab, -1 for the previous one; wraps around.
  step: 1 | -1;
}
